import React, { useEffect, useState } from 'react';
import { Users, Skull, HeartPulse, AlertTriangle, Home } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import communityRegistryApi, { OverviewResponse } from '../../services/communityRegistryApi';

const KPI_DEFS: Array<{
  key: keyof OverviewResponse['kpis'];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  format?: (v: number) => string;
}> = [
  { key: 'total_households_visited', label: 'Households Visited', icon: Home, color: 'text-blue-600' },
  { key: 'average_household_size', label: 'Avg. Household Size', icon: Users, color: 'text-emerald-600', format: (v) => v.toFixed(1) },
  { key: 'suspected_illness_reported', label: 'Suspected Illness', icon: AlertTriangle, color: 'text-amber-600' },
  { key: 'illness_confirmed', label: 'Confirmed Illness', icon: HeartPulse, color: 'text-red-600' },
  { key: 'total_deaths_reported', label: 'Deaths Reported', icon: Skull, color: 'text-slate-700' },
];

interface Props {
  refreshKey?: number;
}

const OverviewTab: React.FC<Props> = ({ refreshKey = 0 }) => {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    communityRegistryApi
      .getOverview()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load overview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {KPI_DEFS.map(({ key, label, icon: Icon, color, format }) => (
          <Card key={key}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
              <Icon className={`h-4 w-4 ${color}`} />
            </CardHeader>
            <CardContent>
              {loading || !data ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-2xl font-bold">
                  {format ? format(data.kpis[key] as number) : data.kpis[key]}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Demographics</CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !data ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Average household size</span>
                  <span className="text-lg font-semibold">{data.demographics.average_household_size.toFixed(1)}</span>
                </div>
                <div className="space-y-2">
                  <span className="text-sm text-muted-foreground">Gender distribution</span>
                  {data.demographics.gender_distribution.length === 0 && (
                    <div className="text-sm text-muted-foreground">No data</div>
                  )}
                  {data.demographics.gender_distribution.map((g) => (
                    <div key={g.id} className="flex items-center gap-2">
                      <div className="w-24 text-sm capitalize">{g.label}</div>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-blue-600"
                          style={{ width: `${Math.min(100, g.value)}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-sm">{g.value.toFixed(0)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Household Map</CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !data ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                {Array.isArray(data.map?.points)
                  ? `${data.map.points.length} household location(s) — map view coming soon`
                  : 'Map data available via /overview?map_mode=choropleth'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OverviewTab;
